import React, { useState, useEffect } from 'react';

import BaseModal, { BaseModalProps } from '../BaseModal/BaseModal';
import Button from '../Button';
import Input from '../Input';
import {
  ShareSettingsForm,
  ShareSettings,
  defaultShareSettings,
  shareSettingsFromTemplate,
  buildDownloadOptions,
  buildPreviewOptions,
  buildBranding,
  daysToExpiryValue,
  expiryValueToDays,
  EXPIRY_OPTIONS,
} from '../ShareSettingsForm';

import {
  useCreateTemplateMutation,
  useUpdateTemplateMutation,
} from 'src/api/mutations/templateMutations';
import { ShareTemplate, CreateTemplateRequest } from 'src/types/TShare';
import { useAlerts } from 'src/common/AlertManager';
import useErrorMessages from 'src/hooks/useErrorsMessage';
import useModal from 'src/hooks/useModal';
import { usePrintExportAvailable } from 'src/hooks/usePrintExportAvailable';

import styles from './TemplateFormModal.module.scss';

type TemplateFormModalProps = BaseModalProps & {
  template?: ShareTemplate;
  onSuccess?: () => void;
};

export const TemplateFormModal: React.FC<TemplateFormModalProps> = ({
  template,
  onSuccess,
  ...modalProps
}) => {
  const { closeModal } = useModal();
  const { showAlert, showSuccess } = useAlerts();
  const { getErrorMessage } = useErrorMessages();
  const printExportAvailable = usePrintExportAvailable();

  const isEditMode = !!template;

  // Basic info (template-level) state
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [defaultExpiry, setDefaultExpiry] = useState<string>('WEEK');

  // Shared branding / preview / download settings
  const [settings, setSettings] = useState<ShareSettings>(defaultShareSettings());

  useEffect(() => {
    if (template) {
      setName(template.name || '');
      setDescription(template.description || '');
      setDefaultExpiry(daysToExpiryValue(template.default_expiry_days));
      setSettings(shareSettingsFromTemplate(template));
    }
  }, [template]);

  const createMutation = useCreateTemplateMutation({
    onSuccess: () => {
      showSuccess('Template created successfully');
      closeModal();
      onSuccess?.();
    },
    onError: (error) => {
      showAlert(getErrorMessage(error.message));
    },
  });

  const updateMutation = useUpdateTemplateMutation(template?.template_id || '', {
    onSuccess: () => {
      showSuccess('Template updated successfully');
      closeModal();
      onSuccess?.();
    },
    onError: (error) => {
      showAlert(getErrorMessage(error.message));
    },
  });

  const handleSubmit = () => {
    if (!name.trim()) {
      showAlert('Please enter a template name');
      return;
    }

    const data: CreateTemplateRequest = {
      name: name.trim(),
      description: description.trim() || undefined,
      default_expiry_days: expiryValueToDays(defaultExpiry),
      allow_feedback: settings.allowFeedback,
      allow_approval: settings.allowApproval,
      branding: buildBranding(settings),
      download_options: buildDownloadOptions(settings),
      preview_options: buildPreviewOptions(settings),
    };

    if (isEditMode) {
      updateMutation.mutate(data);
    } else {
      createMutation.mutate(data);
    }
  };

  const isLoading = createMutation.isLoading || updateMutation.isLoading;

  return (
    <BaseModal {...modalProps}>
      <h3 className={styles.modalTitle}>
        {isEditMode ? 'Edit Template' : 'Create Template'}
      </h3>
      <p className={styles.modalDescription}>
        {isEditMode
          ? 'Update your template settings'
          : 'Create a reusable template for your shares'}
      </p>

      <div className={styles.form}>
        <div className={styles.formScrollArea}>
          {/* Basic Info Section */}
          <div className={styles.sectionCard}>
            <h4 className={styles.sectionTitle}>Basic Information</h4>
            <div className={styles.sectionContent}>
              <div className={styles.formSection}>
                <Input
                  label="Name *"
                  placeholder="Enter template name"
                  value={name}
                  onChange={setName}
                />
              </div>

              <div className={styles.formSection}>
                <Input
                  label="Description"
                  placeholder="Enter description (optional)"
                  value={description}
                  onChange={setDescription}
                />
              </div>

              <div className={styles.formSection}>
                <label className={styles.formLabel}>Default Expiry</label>
                <select
                  className={styles.selectInput}
                  value={defaultExpiry}
                  onChange={(e) => setDefaultExpiry(e.target.value)}
                >
                  {EXPIRY_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>

          {/* Shared branding / preview / download controls */}
          <ShareSettingsForm
            value={settings}
            onChange={setSettings}
            showPrintReady={printExportAvailable}
          />
        </div>
      </div>

      <div className={styles.actions}>
        <Button mode="tertiary" size="small" onClick={closeModal}>
          Cancel
        </Button>
        <Button
          mode="primary"
          size="small"
          onClick={handleSubmit}
          loading={isLoading}
          disabled={!name.trim() || isLoading}
        >
          {isEditMode ? 'Save Changes' : 'Create Template'}
        </Button>
      </div>
    </BaseModal>
  );
};

export default TemplateFormModal;
